import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Info, Activity, Clock, BarChart3 } from 'lucide-react';
import { api } from '../../../services/api';
import { HealthScoreCircle } from './HealthBadge';

interface StoreHealthMetricsProps {
  storeId: string;
}

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function MetricBar({
  label,
  value,
  total,
  color = 'blue',
}: {
  label: string;
  value: number;
  total: number;
  color?: 'blue' | 'green' | 'amber' | 'red';
}) {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
  };

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium">
          {value.toLocaleString()} / {total.toLocaleString()} ({percentage}%)
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} rounded-full transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function IssueSeverityBadge({ severity }: { severity: 'critical' | 'warning' | 'info' }) {
  const config = {
    critical: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'Critical' },
    warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Warning' },
    info: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Info' },
  };

  const { icon: Icon, color, bg, label } = config[severity];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${bg} ${color}`}>
      <Icon size={12} />
      {label}
    </span>
  );
}

export function StoreHealthMetrics({ storeId }: StoreHealthMetricsProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-store-browser-health', storeId],
    queryFn: () => api.getAdminStoreBrowserHealth(storeId),
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-24 bg-gray-200 rounded-xl" />
        <div className="h-48 bg-gray-200 rounded-xl" />
        <div className="h-32 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load health metrics</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Health */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Store Health Report</h3>
            <p className="text-sm text-gray-500">Generated {new Date(data.report_date).toLocaleString()}</p>
          </div>
          <HealthScoreCircle score={data.health_score} size={80} strokeWidth={6} />
        </div>

        {/* Issues */}
        {data.issues.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Issues Found</h4>
            <div className="space-y-2">
              {data.issues.map((issue, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <IssueSeverityBadge severity={issue.severity} />
                  <div className="flex-1">
                    <p className="text-sm text-gray-700">{issue.message}</p>
                    {issue.affected_count && (
                      <p className="text-xs text-gray-500 mt-1">
                        {issue.affected_count} items affected
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.issues.length === 0 && (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
            <CheckCircle className="text-green-600" size={24} />
            <div>
              <p className="font-medium text-green-700">No issues found</p>
              <p className="text-sm text-green-600">This store's data is healthy</p>
            </div>
          </div>
        )}
      </div>

      {/* As-Is Metrics */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Activity className="text-blue-600" size={20} />
          As-Is Data Quality
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Completeness */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Completeness</h4>
            <MetricBar
              label="With Price"
              value={data.asis.completeness.with_price.count}
              total={data.asis.completeness.with_price.count + (100 - data.asis.completeness.with_price.percentage) / data.asis.completeness.with_price.percentage * data.asis.completeness.with_price.count || 0}
              color={data.asis.completeness.with_price.percentage >= 90 ? 'green' : 'amber'}
            />
            <MetricBar
              label="With Image"
              value={data.asis.completeness.with_image.count}
              total={data.asis.completeness.with_image.count + (100 - data.asis.completeness.with_image.percentage) / data.asis.completeness.with_image.percentage * data.asis.completeness.with_image.count || 0}
              color={data.asis.completeness.with_image.percentage >= 80 ? 'green' : 'amber'}
            />
            <MetricBar
              label="Fully Complete"
              value={data.asis.completeness.fully_complete.count}
              total={data.asis.completeness.with_price.count}
              color={data.asis.completeness.fully_complete.percentage >= 80 ? 'green' : 'amber'}
            />
          </div>

          {/* Freshness */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Freshness</h4>
            <MetricBar
              label="Updated 24h"
              value={data.asis.freshness.updated_24h.count}
              total={data.asis.freshness.updated_7d.count + data.asis.freshness.stale.count}
              color={data.asis.freshness.updated_24h.percentage >= 50 ? 'green' : 'amber'}
            />
            <MetricBar
              label="Updated 7d"
              value={data.asis.freshness.updated_7d.count}
              total={data.asis.freshness.updated_7d.count + data.asis.freshness.stale.count}
              color={data.asis.freshness.updated_7d.percentage >= 80 ? 'green' : 'amber'}
            />
            <MetricBar
              label="Stale (>7d)"
              value={data.asis.freshness.stale.count}
              total={data.asis.freshness.updated_7d.count + data.asis.freshness.stale.count}
              color={data.asis.freshness.stale.percentage > 20 ? 'red' : 'green'}
            />
          </div>
        </div>

        {/* Accuracy */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Seed Accuracy</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-semibold text-gray-900">{data.asis.accuracy.seed_products_found}</p>
              <p className="text-xs text-gray-500">Seed Found</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">{data.asis.accuracy.actual_product_count}</p>
              <p className="text-xs text-gray-500">In Database</p>
            </div>
            <div>
              <p className={`text-2xl font-semibold ${data.asis.accuracy.match_rate >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                {data.asis.accuracy.match_rate}%
              </p>
              <p className="text-xs text-gray-500">Match Rate</p>
            </div>
          </div>
          {data.asis.accuracy.last_seed_run && (
            <p className="text-xs text-gray-400 mt-3 text-center">
              Last run: {formatTimeAgo(data.asis.accuracy.last_seed_run)} ({data.asis.accuracy.last_seed_status})
            </p>
          )}
        </div>
      </div>

      {/* Clearance Metrics */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="text-orange-600" size={20} />
          Clearance Data Quality
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Completeness</h4>
            <MetricBar
              label="With Price"
              value={data.clearance.completeness.with_price.count}
              total={data.clearance.completeness.with_price.count + (100 - data.clearance.completeness.with_price.percentage) / data.clearance.completeness.with_price.percentage * data.clearance.completeness.with_price.count || 0}
              color={data.clearance.completeness.with_price.percentage >= 90 ? 'green' : 'amber'}
            />
            <MetricBar
              label="Fully Complete"
              value={data.clearance.completeness.fully_complete.count}
              total={data.clearance.completeness.with_price.count}
              color={data.clearance.completeness.fully_complete.percentage >= 80 ? 'green' : 'amber'}
            />
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Freshness</h4>
            <MetricBar
              label="Updated 7d"
              value={data.clearance.freshness.updated_7d.count}
              total={data.clearance.freshness.updated_7d.count + data.clearance.freshness.stale.count}
              color={data.clearance.freshness.updated_7d.percentage >= 80 ? 'green' : 'amber'}
            />
            <MetricBar
              label="Stale (>7d)"
              value={data.clearance.freshness.stale.count}
              total={data.clearance.freshness.updated_7d.count + data.clearance.freshness.stale.count}
              color={data.clearance.freshness.stale.percentage > 20 ? 'red' : 'green'}
            />
          </div>
        </div>
      </div>

      {/* Physical Scan Metrics */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="text-purple-600" size={20} />
          Physical Scan Statistics
        </h3>

        <div className="grid grid-cols-3 gap-4 text-center mb-6">
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-2xl font-semibold text-purple-600">{data.physical.scan_stats.total_scans}</p>
            <p className="text-xs text-purple-700">Total Scans</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-2xl font-semibold text-purple-600">{data.physical.scan_stats.unique_products}</p>
            <p className="text-xs text-purple-700">Unique Products</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-2xl font-semibold text-purple-600">
              {Math.round(data.physical.scan_stats.avg_confidence * 100)}%
            </p>
            <p className="text-xs text-purple-700">Avg Confidence</p>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Freshness</h4>
          <MetricBar
            label="Seen in 7d"
            value={data.physical.freshness.updated_7d.count}
            total={data.physical.freshness.updated_7d.count + data.physical.freshness.stale.count}
            color={data.physical.freshness.updated_7d.percentage >= 50 ? 'green' : 'amber'}
          />
          <MetricBar
            label="Stale (>7d)"
            value={data.physical.freshness.stale.count}
            total={data.physical.freshness.updated_7d.count + data.physical.freshness.stale.count}
            color={data.physical.freshness.stale.percentage > 50 ? 'red' : 'green'}
          />
        </div>
      </div>

      {/* Recent Runs */}
      {data.recent_runs.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Seed Runs</h3>
          <div className="space-y-2">
            {data.recent_runs.map((run) => (
              <div
                key={run.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    run.status === 'success'
                      ? 'bg-green-100 text-green-700'
                      : run.status === 'error'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {run.status}
                  </span>
                  <span className="text-sm font-medium text-gray-700 capitalize">{run.run_type}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">{run.products_found} products</p>
                  <p className="text-xs text-gray-400">{formatTimeAgo(run.started_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
